import React, { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { LiveChannel } from '@lumos/contracts';
import { advanceNowNext } from '../pages/epg-now.js';
import { AirBar, StarGlyph } from './ChannelLogo.js';
import { BigLogo, toneStyle } from './BigLogo.js';
import { useLogoLook } from './logo-look.js';
import { airedShare, clockLabel, useNowNext, useWallClock } from './store.js';
import { Marquee } from '../components/Marquee.js';

/** Запас сверху и снизу видимой части: плитки успевают появиться до прокрутки к ним. */
const OVERSCAN_PX = 420;
/** Сколько мышь должна задержаться на плитке, чтобы панель переключилась на неё. */
const HOVER_MS = 220;
/** После прокрутки плитки «наезжают» на неподвижную мышь — это не наведение. */
const SCROLL_QUIET_MS = 250;

interface Layout {
  cols: number;
  tileH: number;
  gap: number;
}

/**
 * Раскладка по ширине: плитка не уже `minW` (на 1920 слева от панели — четыре
 * колонки, на телефоне — две), высота — от ширины, в пределах, где помещаются
 * логотип, название и строка «что идёт».
 */
function layoutFor(width: number): Layout {
  const viewport = window.innerWidth;
  const phone = viewport < 600;
  const gap = phone ? 12 : 16;
  const minW = phone ? 150 : Math.max(200, viewport * 0.125);
  const cols = Math.max(2, Math.floor((width + gap) / (minW + gap)));
  const tileW = (width - gap * (cols - 1)) / cols;
  const tileH = Math.round(Math.min(236, Math.max(phone ? 136 : 150, tileW * 0.72)));
  return { cols, tileH, gap };
}

function canFocusVisible(element: HTMLElement): boolean {
  try {
    return element.matches(':focus-visible');
  } catch {
    return false;
  }
}

/**
 * Сетка плиток каналов. Виртуальная по строкам: каналов под две тысячи, а в
 * DOM — только видимые строки (плюс строка выбранной плитки и плитки в фокусе,
 * чтобы фокус и Tab не терялись). «Сейчас/далее» плитки спрашивают сами, и
 * пакетом — только смонтированные (`useNowNext`).
 *
 * Прокрутка — своя (`ownScroll`, широкий экран: страница не листается, рядом
 * стоит панель) или окна (узкий экран).
 *
 * Клавиатура: стрелки — по плиткам, «вверх» из первой строки — к группам,
 * «вправо» из последней колонки — в панель, Enter — смотреть. Фокус с
 * клавиатуры и задержавшаяся мышь выбирают плитку; нажатие решает страница
 * (`onActivate`: ей важно, касание это или нет). Звезда в углу плитки —
 * избранное (`onToggleFavorite`), канал она не запускает.
 */
export function TileGrid({
  channels,
  selectedId,
  busyId,
  ownScroll,
  onPick,
  onActivate,
  onToggleFavorite,
  onLeaveTop,
  onLeaveRight,
}: {
  channels: LiveChannel[];
  selectedId: string | null;
  busyId: string | null;
  ownScroll: boolean;
  onPick: (channel: LiveChannel) => void;
  /** `touch` — нажали пальцем (или пером): первое касание только выбирает. */
  onActivate: (channel: LiveChannel, touch: boolean) => void;
  onToggleFavorite: (channel: LiveChannel) => void;
  onLeaveTop: () => void;
  onLeaveRight?: () => void;
}): React.JSX.Element {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const [layout, setLayout] = useState<Layout | null>(null);
  const [range, setRange] = useState<{ from: number; to: number }>({ from: 0, to: 4 });
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const lastScroll = useRef(0);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const update = (): void => {
      const next = layoutFor(canvas.clientWidth);
      setLayout((prev) =>
        prev !== null && prev.cols === next.cols && prev.tileH === next.tileH && prev.gap === next.gap ? prev : next,
      );
    };
    update();
    if (typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver(update);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  const rowH = layout === null ? 1 : layout.tileH + layout.gap;
  const cols = layout?.cols ?? 1;
  const rowCount = Math.ceil(channels.length / cols);

  const measure = useCallback((): void => {
    const canvas = canvasRef.current;
    if (canvas === null || layout === null) return;
    let top: number;
    let height: number;
    if (ownScroll && scrollRef.current !== null) {
      top = scrollRef.current.scrollTop;
      height = scrollRef.current.clientHeight;
    } else {
      top = -canvas.getBoundingClientRect().top;
      height = window.innerHeight;
    }
    const step = layout.tileH + layout.gap;
    const from = Math.max(0, Math.floor((top - OVERSCAN_PX) / step));
    const to = Math.max(from + 1, Math.ceil((top + height + OVERSCAN_PX) / step));
    setRange((prev) => (prev.from === from && prev.to === to ? prev : { from, to }));
  }, [layout, ownScroll]);

  useLayoutEffect(() => {
    measure();
    let frame = 0;
    const onScroll = (): void => {
      lastScroll.current = Date.now();
      if (frame !== 0) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    };
    const target: HTMLElement | Window = ownScroll && scrollRef.current !== null ? scrollRef.current : window;
    target.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      target.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, [measure, ownScroll]);

  const selectedIndex = selectedId === null ? -1 : channels.findIndex((channel) => channel.id === selectedId);
  const tabStop = selectedIndex >= 0 ? selectedIndex : 0;

  const rows: number[] = [];
  for (let row = range.from; row < Math.min(rowCount, range.to); row += 1) rows.push(row);
  for (const index of [tabStop, focusIndex]) {
    if (index === null || index >= channels.length) continue;
    const row = Math.floor(index / cols);
    if (!rows.includes(row)) rows.push(row);
  }
  // По порядку: иначе строка выбранной плитки, вошедшая в видимую часть,
  // переставлялась бы в DOM — и терялись бы фокус и нажатие на ней.
  rows.sort((a, b) => a - b);

  const pendingFocus = useRef<number | null>(null);
  useLayoutEffect(() => {
    const index = pendingFocus.current;
    if (index === null) return;
    pendingFocus.current = null;
    const target = canvasRef.current?.querySelector<HTMLElement>(`.tv-tile[data-index="${index}"]`);
    if (!target) return;
    target.focus({ preventScroll: true });
    target.scrollIntoView({ block: 'nearest' });
  });

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const target = event.target as HTMLElement;
    if (!target.classList.contains('tv-tile')) return;
    const index = Number(target.dataset.index);
    const last = channels.length - 1;
    let next: number | null = null;
    switch (event.key) {
      case 'Enter': {
        // Сами, а не встроенным щелчком кнопки: Enter — всегда «смотреть».
        const channel = channels[index];
        if (channel === undefined || event.repeat) return;
        event.preventDefault();
        onActivate(channel, false);
        return;
      }
      case 'ArrowRight':
        if ((index + 1) % cols === 0 || index === last) {
          if (onLeaveRight !== undefined) {
            event.preventDefault();
            onLeaveRight();
          }
          return;
        }
        next = index + 1;
        break;
      case 'ArrowLeft':
        if (index % cols === 0) return;
        next = index - 1;
        break;
      case 'ArrowDown':
        if (index + cols <= last) next = index + cols;
        else if (Math.floor(index / cols) < Math.floor(last / cols)) next = last;
        else return;
        break;
      case 'ArrowUp':
        if (index < cols) {
          event.preventDefault();
          onLeaveTop();
          return;
        }
        next = index - cols;
        break;
      default:
        return;
    }
    event.preventDefault();
    pendingFocus.current = next;
    setFocusIndex(next);
  };

  /* Мышь: панель переключается на плитку, на которой мышь задержалась. */
  const hoverTimer = useRef<number | null>(null);
  const onHover = useCallback(
    (channel: LiveChannel): void => {
      if (hoverTimer.current !== null) window.clearTimeout(hoverTimer.current);
      // Пока сетка листается, ждём: выбирается та плитка, на которой мышь
      // осталась, когда прокрутка улеглась.
      const settle = (): void => {
        const quiet = Date.now() - lastScroll.current;
        if (quiet < SCROLL_QUIET_MS) {
          hoverTimer.current = window.setTimeout(settle, SCROLL_QUIET_MS - quiet);
          return;
        }
        hoverTimer.current = null;
        onPick(channel);
      };
      hoverTimer.current = window.setTimeout(settle, HOVER_MS);
    },
    [onPick],
  );
  const onUnhover = useCallback((): void => {
    if (hoverTimer.current !== null) window.clearTimeout(hoverTimer.current);
    hoverTimer.current = null;
  }, []);
  useEffect(() => onUnhover, [onUnhover]);

  const canvas = (
    <div
      ref={canvasRef}
      className="tv-tiles"
      aria-label="Каналы"
      style={{ height: layout === null ? undefined : Math.max(0, rowCount * rowH - (layout?.gap ?? 0)) }}
      onKeyDown={onKeyDown}
    >
      {layout !== null &&
        rows.map((row) => (
          <div
            key={row}
            className="tv-tiles-row"
            style={{
              transform: `translate3d(0, ${row * rowH}px, 0)`,
              height: layout.tileH,
              gap: layout.gap,
              gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
            }}
          >
            {channels.slice(row * cols, row * cols + cols).map((channel, column) => {
              const index = row * cols + column;
              return (
                <Tile
                  key={channel.id}
                  index={index}
                  channel={channel}
                  selected={index === selectedIndex}
                  tabbable={index === tabStop}
                  busy={busyId === channel.id}
                  onActivate={onActivate}
                  onToggleFavorite={onToggleFavorite}
                  onPick={onPick}
                  onHover={onHover}
                  onUnhover={onUnhover}
                />
              );
            })}
          </div>
        ))}
    </div>
  );

  if (!ownScroll) return canvas;
  return (
    <div ref={scrollRef} className="tv-tiles-scroll">
      {canvas}
    </div>
  );
}

/**
 * Плитка канала: крупный логотип, название, «14:20  Передача» и полоса эфира.
 * У выбранной плитки не влезшие название и передача идут бегущей строкой.
 * Фон — тёмная поверхность с отсветом цвета логотипа: картинок у передач нет.
 *
 * Звезда избранного — соседняя кнопка поверх угла плитки, а не вложенная:
 * кнопка в кнопке недопустима. Наведение и уход мыши считаются по ячейке
 * целиком, чтобы переход со плитки на звезду не сбрасывал выбор.
 */
const Tile = memo(function Tile({
  index,
  channel,
  selected,
  tabbable,
  busy,
  onActivate,
  onToggleFavorite,
  onPick,
  onHover,
  onUnhover,
}: {
  index: number;
  channel: LiveChannel;
  selected: boolean;
  tabbable: boolean;
  busy: boolean;
  onActivate: (channel: LiveChannel, touch: boolean) => void;
  onToggleFavorite: (channel: LiveChannel) => void;
  onPick: (channel: LiveChannel) => void;
  onHover: (channel: LiveChannel) => void;
  onUnhover: () => void;
}): React.JSX.Element {
  const look = useLogoLook(channel.logoUrl);
  /** Чем нажали: щелчок кнопки сам этого не говорит. */
  const pointer = useRef<string | null>(null);
  /** Счётчик нажатий звезды: новый ключ перезапускает «щелчок» звезды. */
  const [pops, setPops] = useState(0);
  const favorite = channel.favorite;
  return (
    <div
      className="tv-tile-cell"
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') onHover(channel);
      }}
      onPointerLeave={onUnhover}
    >
      <button
        type="button"
        className={selected ? 'tv-tile is-selected' : 'tv-tile'}
        data-index={index}
        tabIndex={tabbable ? 0 : -1}
        aria-current={selected ? 'true' : undefined}
        style={toneStyle(look)}
        onPointerDown={(event) => {
          pointer.current = event.pointerType;
        }}
        onClick={() => {
          const touch = pointer.current === 'touch' || pointer.current === 'pen';
          pointer.current = null;
          onActivate(channel, touch);
        }}
        onFocus={(event) => {
          if (canFocusVisible(event.currentTarget)) onPick(channel);
        }}
      >
        <BigLogo url={channel.logoUrl} name={channel.name} look={look} className="tv-tile-logo" />
        {/* Длинное название канала и передачи у выбранной плитки (под курсором
            или в фокусе) идёт бегущей строкой; у остальных — многоточие. */}
        <Marquee className="tv-tile-name" text={channel.name} active={selected} />
        {busy ? (
          <span className="tv-tile-now is-busy">Подключение…</span>
        ) : (
          <TileAiring epgId={channel.epgId} active={selected} />
        )}
      </button>
      <button
        type="button"
        className={favorite ? 'tv-tile-star is-on' : 'tv-tile-star'}
        aria-pressed={favorite}
        aria-label={favorite ? 'Убрать из избранного' : 'Добавить в избранное'}
        title={favorite ? 'Убрать из избранного' : 'Добавить в избранное'}
        tabIndex={tabbable ? 0 : -1}
        onClick={(event) => {
          event.stopPropagation();
          setPops((value) => value + 1);
          onToggleFavorite(channel);
        }}
      >
        <span key={pops} className={pops > 0 ? 'tv-tile-star-glyph is-pop' : 'tv-tile-star-glyph'}>
          <StarGlyph filled={favorite} />
        </span>
      </button>
    </div>
  );
});

/**
 * «Сейчас» плитки — отдельным компонентом: часы тикают раз в полминуты и
 * перерисовывают только видимые плитки, а не сетку.
 */
function TileAiring({ epgId, active }: { epgId: string | null; active: boolean }): React.JSX.Element {
  const wallClock = useWallClock();
  const airing = advanceNowNext(useNowNext(epgId, wallClock), wallClock);
  const now = airing?.now ?? null;
  const next = airing?.next ?? null;
  if (now === null) {
    // Между передачами — что начнётся следующей; без программы — честно «нет».
    return (
      <>
        {next !== null ? (
          <span className="tv-tile-now is-muted">
            <span className="tv-tile-time">{clockLabel(next.start)}</span>
            <Marquee className="tv-tile-title" text={next.title} active={active} />
          </span>
        ) : (
          <span className="tv-tile-now is-muted">{epgId === null ? 'Нет программы' : 'Программа скоро'}</span>
        )}
        <span className="tv-air tv-tile-air" aria-hidden="true" />
      </>
    );
  }
  return (
    <>
      <span className="tv-tile-now">
        <span className="tv-tile-time">{clockLabel(now.start)}</span>
        <Marquee className="tv-tile-title" text={now.title} active={active} />
      </span>
      <AirBar share={airedShare(now, wallClock)} className="tv-tile-air" />
    </>
  );
}
